// src/components/auth/AdminLogin.jsx
import React, { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';

// SVG Icons Components (similar to reference)
const EmailIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

const LockIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const EyeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
  </svg>
);

const EyeOffIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
  </svg>
);

const LoadingSpinner = ({ className = "w-5 h-5" }) => (
  <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
  </svg>
);

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

const ErrorIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'error' or 'success'
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);

  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear login message when user starts typing
    if (loginMessage) {
      setLoginMessage('');
      setMessageType('');
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaVerified(!!token);
  };

  const handleRecaptchaError = () => {
    console.error('reCAPTCHA error occurred');
    setMessageType('error');
    setLoginMessage('reCAPTCHA verification failed. Please refresh the page and try again.');
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaVerified(false);
    setMessageType('error');
    setLoginMessage('reCAPTCHA has expired. Please verify again.');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Get reCAPTCHA site key with fallback
  const getRecaptchaSiteKey = () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    
    // Test key for development (always passes)
    if (!siteKey) {
      return '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
    }
    
    return siteKey;
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Reset reCAPTCHA safely
  const resetRecaptcha = () => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
      } catch (error) {
        console.error('Error resetting reCAPTCHA:', error);
      }
    }
    setRecaptchaVerified(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // This prevents the default form submission and page refresh
    
    if (!validateForm()) {
      return;
    }

    if (!recaptchaVerified && process.env.NODE_ENV === 'production') {
      setMessageType('error');
      setLoginMessage('Please complete the reCAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setLoginMessage('');
    setMessageType('');

    try {
      // Get reCAPTCHA token (use test token in development)
      let recaptchaToken = 'test-token';
      
      if (process.env.NODE_ENV === 'production') {
        if (!recaptchaRef.current) {
          throw new Error('reCAPTCHA not initialized');
        }
        recaptchaToken = recaptchaRef.current.getValue();
        if (!recaptchaToken) {
          throw new Error('reCAPTCHA token not found');
        }
      }

      console.log('Sending login request for:');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000/api'}/editors/editorsLogin`,
        {
          email: formData.email,
          pass: formData.password,
          recaptchaToken: recaptchaToken
        }
      );


      // Check if response data exists and status is success
      if (response.data && response.data.status === 'success') {
        // Use the login function from AuthContext
        const loginResult = await login(
          formData.email, 
          formData.password, 
          'admin',
          response.data.token,
          response.data.user
        );

        if (loginResult.success) {
          setMessageType('success');
          setLoginMessage('Login successful! Redirecting...');
          const returnUrl = searchParams.get('returnUrl');
          setTimeout(() => {
            navigate(returnUrl || '/editors/dashboard');
          }, 1000);
        } else {
          setMessageType('error');
          setLoginMessage(loginResult.error || 'Login failed');
          resetRecaptcha();
        }
      } else {
        // This case shouldn't happen with proper error handling, but just in case
        setMessageType('error');
        setLoginMessage(response.data?.message || 'Login failed');
        resetRecaptcha();
      }
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      // Handle different error scenarios
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorData = error.response.data;
        const statusCode = error.response.status;
        
        console.log('Error status code:', statusCode);
        console.log('Error data:', errorData);
        
        if (statusCode === 401) {
          // Invalid credentials
          setMessageType('error');
          setLoginMessage(errorData?.message || 'Invalid email or password');
        } else if (statusCode === 400) {
          // Bad request - validation errors
          setMessageType('error');
          setLoginMessage(errorData?.message || 'Please check your input');
        } else if (statusCode === 404) {
          // User not found
          setMessageType('error');
          setLoginMessage(errorData?.message || 'Invalid email or password');
        } else if (statusCode === 500) {
          // Server error
          setMessageType('error');
          setLoginMessage('Server error. Please try again later.');
        } else {
          // Other errors
          setMessageType('error');
          setLoginMessage(errorData?.message || 'Login failed. Please try again.');
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        setMessageType('error');
        setLoginMessage('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
        setMessageType('error');
        setLoginMessage(error.message || 'Login failed. Please try again.');
      }
      
      // Reset reCAPTCHA on error
      resetRecaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6ecf6]">
      <div className="container-fluid h-screen">
        <div className="row h-full flex flex-wrap">
          {/* Left Column - Login Form */}
          <div className="w-full lg:w-1/2 md:w-1/2 base-login-container base-form-holder flex items-center justify-center p-8">
            <div className="form-holder max-w-md w-full">
              {/* Logo/Brand */}
              <div className="bwt-logo text-2xl font-bold text-[--asfi-purple] mb-8 text-center md:text-left">
                AFRICAN SCIENCE FRONTIERS INITIATIVES RESEARCH JOURNAL | <span className="text-[--asfi-blue]">Editor</span>
              </div>

              {/* Login Message */}
              {loginMessage && (
                <div className={`mb-4 p-3 rounded-lg flex items-start ${
                  messageType === 'success' 
                    ? 'bg-green-100 border border-green-400 text-green-700' 
                    : 'bg-red-100 border border-red-400 text-red-700'
                }`}>
                  {messageType === 'success' ? (
                    <CheckCircleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  ) : (
                    <ErrorIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  )}
                  <span className="text-sm">{loginMessage}</span>
                </div>
              )}

              {/* Login Form */}
              <form
                id="login-form"
                onSubmit={handleSubmit}
                noValidate
                className="space-y-6"
              >
                <fieldset className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <EmailIcon className="w-5 h-5" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 pl-10 border ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                  )}
                </fieldset>

                <fieldset className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <LockIcon className="w-5 h-5" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full px-4 py-3 pl-10 pr-10 border ${
                        errors.password ? 'border-red-500' : 'border-gray-300'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      disabled={isSubmitting}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="w-5 h-5" />
                      ) : (
                        <EyeIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">{errors.password}</p>
                  )}
                </fieldset>

                {/* reCAPTCHA */}
                <fieldset className="form-group flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={getRecaptchaSiteKey()}
                    onChange={handleRecaptchaChange}
                    onErrored={handleRecaptchaError}
                    onExpired={handleRecaptchaExpired}
                    theme="light"
                  />
                </fieldset>

                {/* Development mode notice */}
                {process.env.NODE_ENV === 'development' && !process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                  <div className="mt-2 p-2 bg-yellow-50 text-yellow-800 text-sm rounded-lg border border-yellow-200">
                    ⚠️ Using test reCAPTCHA key. In production, add REACT_APP_RECAPTCHA_SITE_KEY to .env
                  </div>
                )}

                {/* Submit Button - CHANGED TO type="submit" */}
                <fieldset className="form-group">
                  <button
                    type="submit"  // Changed from "button" to "submit"
                    id="loginButton"
                    disabled={!recaptchaVerified || isSubmitting}
                    className={`w-full py-3 px-4 bg-gradient-to-r from-[--asfi-purple] to-[--asfi-blue] text-white font-semibold rounded-lg hover:from-[--asfi-blue] hover:to-[--asfi-purple] focus:outline-none focus:ring-2 focus:ring-[--asfi-purple] focus:ring-offset-2 transition-all duration-200 ${
                      (!recaptchaVerified || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <LoadingSpinner className="w-5 h-5 mr-2" />
                        Signing in...
                      </span>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </fieldset>
              </form>

              {/* Back to Main Site */}
              <div className="text-center mt-8">
                <a href="/" className="text-gray-600 hover:text-[--asfi-purple] text-sm transition-colors">
                  ← Back to ASFIRJ
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Image */}
          <div 
            className="hidden md:block md:w-1/2 lg:w-1/2 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: 'url("https://images.pexels.com/photos/3646172/pexels-photo-3646172.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2")',
              backgroundColor: '#2d3748',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
            title="Photo by Olena Bohovyk: https://www.pexels.com/photo/brown-wooden-shelf-with-books-3646172/"
          >
            {/* Optional overlay for better text contrast if needed */}
            <div className="w-full h-full bg-gradient-to-t from-purple-900/50 to-transparent"></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .container-fluid {
          width: 100%;
          height: 100vh;
        }
        .row {
          display: flex;
          flex-wrap: wrap;
          height: 100%;
        }
        .base-login-container {
          min-height: 100vh;
        }
        .form-control-lg {
          font-size: 1rem;
          transition: all 0.2s ease;
        }
        .form-control-lg:focus {
          transform: translateY(-1px);
        }
        .submit-btn {
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .bwt-logo {
          line-height: 1.4;
        }
        @media (max-width: 768px) {
          .base-login-container {
            min-height: auto;
            padding: 2rem 1rem;
          }
          .hide-on-small-only {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;